import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQueryEndpoints,
    FilterOperators,
    QueryResponse,
    SortDirections,
} from "~/modules/filters/types";
import { AssetTimeseriesColumns } from "~/modules/asset_timeseries_v4/types";
import { TimeSeriesItem } from "~/modules/token_timeseries/types";
import { buildHypotheticalPerformanceFromNav } from "./screenerPerformanceReturns";
import { MEASURE_SLUGS } from "~/consts";

// // // //

export function usePerformanceChartData(assetID: number): {
    chartData: TimeSeriesItem | null;
    isLoading: boolean;
} {
    const performanceReq = useApiQuery({
        endpoint: ApiQueryEndpoints.AssetTimeseriesV4,
        query: {
            aggregate: {
                aggregateFunction: AggregateFunctions.Sum,
                groupBy: "measure",
                interval: AggregateQueryInterval.day,
                groupLimit: 2,
            },
            filter: {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: AssetTimeseriesColumns.assetID,
                        operator: FilterOperators.Equals,
                        value: assetID,
                    },
                    // {
                    //     field: AssetTimeseriesColumns.measureSlug,
                    //     operator: FilterOperators.Equals,
                    //     value: MEASURE_SLUGS.net_asset_value_dollar,
                    // },
                    {
                        operator: FilterOperators.Or,
                        filters: [
                            {
                                field: AssetTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.hypothetical_10_000_performance,
                            },
                            {
                                field: AssetTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.net_asset_value_dollar,
                            },
                        ],
                    },
                ],
            },
            sort: {
                field: AssetTimeseriesColumns.date,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: 800,
            },
        },
    });

    // Pull the response data from the request
    const resp: QueryResponse<TimeSeriesItem> | null =
        performanceReq.data ?? null;

    // Pull the TimeseriesItem[] data from the response
    const performanceData: TimeSeriesItem[] = resp?.results ?? [];

    // Lookup the hypothetical 10K performance data
    const hypotheticalPerformanceData = performanceData.find(
        (item) =>
            item.measure.slug === MEASURE_SLUGS.hypothetical_10_000_performance,
    );

    // Lookup the NAV data
    const navData = performanceData.find(
        (item) => item.measure.slug === MEASURE_SLUGS.net_asset_value_dollar,
    );

    return {
        chartData:
            hypotheticalPerformanceData ??
            buildHypotheticalPerformanceFromNav(navData),
        isLoading: performanceReq.isLoading,
    };
}
